/**
 * Global video mute state context
 * When a user mutes/unmutes one video poll, it applies to all video polls
 */
import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface VideoMuteContextType {
  isMuted: boolean;
  toggleMute: () => void;
  setMuted: (muted: boolean) => void;
}

const VideoMuteContext = createContext<VideoMuteContextType | undefined>(undefined);

export function VideoMuteProvider({ children }: { children: ReactNode }) {
  // Default to muted (standard for autoplay videos)
  const [isMuted, setIsMuted] = useState(true);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  const setMuted = useCallback((muted: boolean) => {
    setIsMuted(muted);
  }, []);

  return (
    <VideoMuteContext.Provider value={{ isMuted, toggleMute, setMuted }}>
      {children}
    </VideoMuteContext.Provider>
  );
}

export function useVideoMute() {
  const context = useContext(VideoMuteContext);
  if (context === undefined) {
    throw new Error('useVideoMute must be used within a VideoMuteProvider');
  }
  return context;
}
